import { FormEvent, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { ApiError, apiFetch } from "../api/client";
import { queryClient } from "../app/queryClient";
import { Panel } from "../components/Panel";
import type { ChatOffer } from "../types";

const emptyOffer = {
  name: "",
  provider: "",
  category: "luce" as "luce" | "gas",
  consumption: "",
  monthly_cost: "",
  expires_on: "",
};

function money(value: number) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(value);
}

function consumptionLabel(offer: ChatOffer) {
  const unit = offer.category === "gas" ? "Smc" : "kWh";
  const formatted = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 1 }).format(offer.consumption);
  return `${formatted} ${unit}`;
}

function italianDate(value: string) {
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

export function OffersPage() {
  const [form, setForm] = useState(emptyOffer);
  const [error, setError] = useState("");
  const { data } = useQuery({
    queryKey: ["offers"],
    queryFn: () => apiFetch<{ data: ChatOffer[] }>("/api/offers"),
  });
  const createOffer = useMutation({
    mutationFn: () =>
      apiFetch("/api/offers", {
        method: "POST",
        body: {
          ...form,
          consumption: form.consumption.replace(",", "."),
          monthly_cost: form.monthly_cost.replace(",", "."),
        },
      }),
    onSuccess: () => {
      setForm(emptyOffer);
      setError("");
      queryClient.invalidateQueries({ queryKey: ["offers"] });
    },
    onError: (reason: unknown) => {
      setError(reason instanceof ApiError ? reason.message : "Non sono riuscito a salvare l'offerta.");
    },
  });
  const deleteOffer = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/offers/${id}`, { method: "DELETE", body: {} }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["offers"] }),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError("");
    createOffer.mutate();
  };

  const offers = data?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold">Offerte</h2>
        <p className="mt-1 text-slate-400">
          Il nome e il prezzo restano nascosti finché il cliente non invia la bolletta. Poi, per luce o per gas, calcoliamo il risparmio con l&apos;offerta dalla spesa mensile più bassa ancora valida.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
        <Panel>
          <div className="space-y-4">
            {offers.length === 0 ? (
              <p className="text-sm text-slate-400">Nessuna offerta caricata. Finché non ce n&apos;è una valida, la chat non ne propone.</p>
            ) : null}
            {offers.map((offer) => (
              <div key={offer.id} className="rounded-2xl border border-white/10 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-lg font-semibold">{offer.name}</p>
                    <p className="mt-1 text-sm text-slate-300">{offer.provider}</p>
                    <p className="mt-3 text-sm leading-6 text-slate-400">
                      {offer.category === "gas" ? "Gas" : "Luce"} · {consumptionLabel(offer)} · {money(offer.monthly_cost)} / mese
                    </p>
                    <p className="mt-1 text-sm text-slate-400">Scade il {italianDate(offer.expires_on)}</p>
                    <p className="mt-3 text-xs uppercase tracking-wide text-emerald-300">
                      {offer.expired ? "Scaduta" : offer.is_current ? "Proposta in chat" : "In elenco"}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteOffer.mutate(offer.id)}
                    className="rounded-xl border border-red-400/20 p-2 text-red-300 hover:bg-red-400/10"
                    type="button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel>
          <form onSubmit={submit} className="space-y-4">
            <h3 className="text-lg font-semibold">Nuova offerta</h3>
            <input
              className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3"
              placeholder="Nome offerta"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
            />
            <input
              className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3"
              placeholder="Fornitore"
              value={form.provider}
              onChange={(event) => setForm({ ...form, provider: event.target.value })}
              required
            />
            <label className="block text-sm text-slate-400">
              Categoria
              <select
                className="mt-2 w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-slate-100"
                value={form.category}
                onChange={(event) => setForm({ ...form, category: event.target.value === "gas" ? "gas" : "luce" })}
              >
                <option value="luce">Luce</option>
                <option value="gas">Gas</option>
              </select>
            </label>
            <label className="block text-sm text-slate-400">
              Consumi ({form.category === "gas" ? "Smc" : "kWh"})
              <input
                className="mt-2 w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-slate-100"
                placeholder={form.category === "gas" ? "es. 1400" : "es. 2700"}
                inputMode="decimal"
                value={form.consumption}
                onChange={(event) => setForm({ ...form, consumption: event.target.value })}
                required
              />
            </label>
            <label className="block text-sm text-slate-400">
              Stima spesa mensile (€)
              <input
                className="mt-2 w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-slate-100"
                placeholder="es. 48"
                inputMode="decimal"
                value={form.monthly_cost}
                onChange={(event) => setForm({ ...form, monthly_cost: event.target.value })}
                required
              />
            </label>
            <label className="block text-sm text-slate-400">
              Scadenza
              <input
                className="mt-2 w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-slate-100"
                type="date"
                value={form.expires_on}
                onChange={(event) => setForm({ ...form, expires_on: event.target.value })}
                required
              />
            </label>
            {error ? <p className="text-sm text-red-300">{error}</p> : null}
            <button
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-400 px-4 py-3 font-semibold text-slate-950 hover:bg-emerald-300 disabled:opacity-60"
              disabled={createOffer.isPending}
            >
              <Plus className="h-4 w-4" />
              Aggiungi offerta
            </button>
          </form>
        </Panel>
      </div>
    </div>
  );
}
